import React, { useState, useCallback } from 'react';
import { LogOut, User, Sun, Moon, ChevronDown, Bell, Plane, Ship, Wallet } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNotifications } from '../../context/NotificationContext';
import { useWallet } from '../../context/WalletContext';
import { useNavigate } from 'react-router-dom';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const Header = ({ title, shipments = [] }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { notifications, unreadCount, markRead, markAllRead, setHighlightedShipment } = useNotifications();
  const { wallet, balanceRupees, isActive } = useWallet();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    if (user && user.id) {
      const logoutTime = new Date().toISOString();
      localStorage.setItem(`lastLogout_${user.id}`, logoutTime);
    }
    logout();
    setShowDropdown(false);
  };

  const handleNotificationClick = useCallback((notification) => {
    const data = notification.data || {};
    const isSeaExport = data.shipmentType === 'Sea-Export';
    const isAccountant = user?.role === 'accountant';

    // Mark as read in DB + local state
    markRead(notification.id);
    setShowNotifications(false);

    // For accountant — highlight the shipment row
    if (isAccountant) {
      // Support both old (awbNo) and new (awbNumber) field names
      const shipmentId = isSeaExport ? data.blNumber : (data.awbNo || data.awbNumber);
      const shipmentType = isSeaExport ? 'Sea-Export' : 'Air-Export';
      if (shipmentId) {
        setHighlightedShipment({ id: shipmentId, type: shipmentType });
      }
      return;
    }

    // For sea export — navigate to dedicated sea shipment detail page
    if (isSeaExport) {
      const blNumber = data.blNumber;
      if (blNumber) {
        navigate(`/sea-shipment/${blNumber}`);
      }
      return;
    }

    // For air export user — navigate to shipment details
    const awbNo = data.awbNo || data.awbNumber;
    if (awbNo) {
      navigate(`/shipment/${awbNo}`);
    }
  }, [user?.role, markRead, setHighlightedShipment, navigate]);

  const handleClearAll = () => {
    markAllRead();
  };

  const parseField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [field];
      } catch {
        return [field];
      }
    }
    return [field];
  };

  const joinWithAnd = (value) => {
    const arr = parseField(value);
    const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
    const unique = [...new Set(filtered)];
    return unique.join(' & ') || '-';
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
      <div className="px-3 sm:px-4 md:px-6 py-0.5 sm:py-1 flex items-center justify-between">
        {/* Logo and Title */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
          <button 
            onClick={() => {
              window.scrollTo(0, 0);
              setTimeout(() => {
                window.location.href = '/';
              }, 100);
            }}
            className="flex-shrink-0 focus:outline-none hover:opacity-80 transition-opacity"
            aria-label="Go to home"
          >
            <img 
              src="/images/logo.png" 
              alt="QuickLook Logo" 
              className="h-14 sm:h-16 md:h-20 w-auto cursor-pointer"
            />
          </button>
        </div>

        {/* Right Side Controls */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
          {/* Notification Bell - For user and accountant roles */}
          {(user?.role === 'user' || user?.role === 'accountant') && (
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                title="Notifications"
              >
                <Bell className="text-gray-600 dark:text-gray-400" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-semibold">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-16 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-96 bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-20 max-h-[70vh] sm:max-h-96 overflow-hidden flex flex-col">
                    <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        New Shipments ({unreadCount})
                      </h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleClearAll}
                          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto overflow-x-hidden">
                      {notifications.filter(n => !n.isRead).length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          <Bell size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No new shipments</p>
                        </div>
                      ) : (
                        notifications.filter(n => !n.isRead).map((notification, index) => {
                          const data = notification.data || {};
                          const isSeaExport = data.shipmentType === 'Sea-Export';
                          const isAccountant = user?.role === 'accountant';
                          
                          // Support both old field names (SSE era) and new field names (FCM/DB era)
                          const shipmentId = isSeaExport
                            ? data.blNumber
                            : (data.awbNo || data.awbNumber);
                          
                          const origin = isSeaExport
                            ? (data.portCode || 'DEL')
                            : (data.origin || 'DEL');
                          
                          const destination = isSeaExport
                            ? (data.portOfDischargeCode || '-')
                            : (data.destination || data.destCode || data.destCity || '-');
                          
                          // Weight — grWeight (old) or grossWeight (new)
                          const weight = isSeaExport
                            ? (data.gwt || '-')
                            : (data.grWeight || data.grossWeight || '-');
                          
                          // Packages — totalPkt (old) or numberOfPackages (new)
                          const packages = isSeaExport
                            ? (data.pkg || '-')
                            : (data.totalPkt || data.numberOfPackages || '-');
                          
                          // Customer label — customer array (old) or customerName string (new)
                          const customerLabel = isAccountant
                            ? (data.username || data.customerName || 'N/A')
                            : isSeaExport
                              ? (data.exporterName || '-')
                              : joinWithAnd(data.customer) || data.customerName || '-';
                          
                          return (
                            <button
                              key={notification.id || index}
                              onClick={() => handleNotificationClick(notification)}
                              className="w-full px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 text-left transition group overflow-hidden hover:bg-blue-50 dark:hover:bg-gray-700/50 cursor-pointer"
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-1 p-2 rounded-lg transition ${
                                  isSeaExport 
                                    ? 'bg-cyan-100 dark:bg-cyan-900/30 group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/50' 
                                    : 'bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50'
                                }`}>
                                  {isSeaExport ? (
                                    <Ship size={18} className="text-cyan-600 dark:text-cyan-400" />
                                  ) : (
                                    <Plane size={18} className="text-blue-600 dark:text-blue-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                        New {isSeaExport ? 'Sea' : 'Air'} Shipment
                                      </p>
                                      <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mb-0.5 break-words whitespace-normal">
                                        <span className="font-medium">{isAccountant ? 'User:' : (isSeaExport ? 'Exporter:' : 'Consignee:')}</span> {customerLabel}
                                      </p>
                                      <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mb-0.5">
                                        Pkt: {packages} | Gr. Wt.: {weight}kg
                                      </p>
                                      {!isAccountant && !isSeaExport && (
                                        <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 truncate">
                                          <span className="font-medium">SB Date:</span> {((parseField(data.sbDate || data.sbDates)[0] || '-').split('T')[0].split(' ')[0])}
                                        </p>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <span className={`text-[10px] sm:text-xs font-medium ${
                                        isSeaExport ? 'text-cyan-600 dark:text-cyan-400' : 'text-blue-600 dark:text-blue-400'
                                      }`}>
                                        {shipmentId || 'N/A'}
                                      </span>
                                      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 justify-end">
                                        <span className="font-medium">{origin}</span>
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                        <span className="font-medium">{destination}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Wallet Balance — user role only, when wallet exists */}
          {user?.role === 'user' && wallet && (
            <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors ${
              isActive
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
                : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400'
            }`}
              title={isActive ? 'Wallet Balance' : 'Wallet Blocked'}
            >
              <Wallet size={13} />
              <span>₹{balanceRupees.toLocaleString('en-IN')}</span>
              {!isActive && <span className="text-[10px] text-red-500 font-medium">Blocked</span>}
            </div>
          )}

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
          >
            {isDarkMode ? (
              <Sun className="text-yellow-500" size={20} />
            ) : (
              <Moon className="text-gray-600" size={20} />
            )}
          </button>

          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center overflow-hidden">
                {user?.profilePicture ? (
                  <img 
                    src={user.profilePicture.startsWith('http') ? user.profilePicture : getCloudFrontUrl(user.profilePicture)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={18} className="text-white" />
                )}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
              <ChevronDown size={16} className="text-gray-600 dark:text-gray-400" />
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-20">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 break-words whitespace-normal">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setShowDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <User size={16} />
                    My Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
